<template>
  <div ref="root" class="c-proxy-tab">
    <p class="c-proxy-tab__description">
      Your organization can connect to Salesforce through a proxy. Crossbeam
      then signs in to Salesforce and syncs your data through the proxy URL.
      Setting, changing or removing it means signing in to Salesforce again.
    </p>
    <SalesforceProxyConnectionFields
      v-model:proxy-url="fields.proxyUrl"
      v-model:client-id="fields.clientId"
      v-model:client-secret="fields.clientSecret"
      :errors="fieldErrors"
      :is-disabled="!canManageDataSources || feedIsReauthorizing"
      :client-secret-placeholder="clientSecretPlaceholder"
      @blur="handleFieldBlur"
    />
    <div class="c-proxy-tab__actions">
      <BittsButton
        v-if="hasStoredProxyUrl(feed)"
        text="Remove and reauthorize"
        type="danger"
        variant="outline"
        :disabled="!canManageDataSources || feedIsReauthorizing"
        data-testid="c-proxy-tab-remove"
        @click="handleRemove"
      />
      <BittsButton
        text="Save and reauthorize"
        :disabled="isSaveDisabled"
        data-testid="c-proxy-tab-save"
        @click="handleSave"
      />
    </div>
    <Teleport to="body">
      <BittsModal
        v-if="pendingAction"
        class="c-proxy-tab__confirm"
        :class="{ 'c-proxy-tab__confirm--summary': pendingAction === 'save' }"
        variant="confirm"
        :icon="confirmCopy.icon"
        :icon-color="confirmCopy.iconColor"
        :title="confirmCopy.title"
        :description="confirmCopy.description"
        :action-loading="feedIsReauthorizing"
        :primary-button-text="confirmCopy.primaryButtonText"
        :primary-button-type="confirmCopy.primaryButtonType"
        secondary-button-text="Cancel"
        data-testid="c-proxy-tab-confirm"
        @action="handleConfirm"
        @closed="closeConfirm"
      >
        <ul
          v-if="pendingAction === 'save'"
          class="c-proxy-tab__summary"
          data-testid="c-proxy-tab-summary"
        >
          <li v-for="item in saveSummary" :key="item.label">
            <span class="c-proxy-tab__summary-label">{{ item.label }}</span>
            {{ item.value }}
          </li>
        </ul>
      </BittsModal>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, type BittsIcon, BittsModal } from '@crossbeam/bitts';

import { computed, ref, useTemplateRef } from 'vue';

import SalesforceProxyConnectionFields from '@/components/data-sources/SalesforceProxyConnectionFields.vue';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import useFeedDetails from '@/composables/useFeedDetails';
import useSalesforceProxyConnection, {
  type SalesforceProxyFieldType,
} from '@/composables/useSalesforceProxyConnection';
import useSalesforceProxyGate from '@/composables/useSalesforceProxyGate';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { focusFirstInvalidField } from '@/utils/focusFirstInvalidField';

type Props = {
  feedId: number;
};
const { feedId } = defineProps<Props>();

const MASKED_CLIENT_SECRET = '••••••••';

type ConfirmActionType = 'save' | 'remove';

const CONFIRM_COPY: Record<
  ConfirmActionType,
  {
    icon: BittsIcon;
    iconColor: 'info' | 'danger';
    title: string;
    description: string;
    primaryButtonText: string;
    primaryButtonType: 'primary' | 'danger';
  }
> = {
  save: {
    icon: ['fas', 'key'],
    iconColor: 'info',
    title: 'Save and reauthorize?',
    description:
      'Crossbeam will use these connection details and send you to Salesforce to sign in again.',
    primaryButtonText: 'Save and reauthorize',
    primaryButtonType: 'primary',
  },
  remove: {
    icon: ['fas', 'trash-can'],
    iconColor: 'danger',
    title: 'Remove the proxy and credentials?',
    description:
      'This removes the Proxy URL, Client ID and Client Secret together, then sends you to Salesforce to sign in again.',
    primaryButtonText: 'Remove and reauthorize',
    primaryButtonType: 'danger',
  },
};

const flashesStore = useFlashesStore();
const { feed } = useDataSourceSettings();
const { reauthorize, feedIsReauthorizing } = useFeedDetails({ feedId });
const { hasStoredProxyUrl } = useSalesforceProxyGate();

const { hasPermission } = useAuth();
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const {
  fields,
  hasChanges,
  isProxyUrlMissing,
  isStoredSecretKept,
  fieldErrors,
  touch,
  changedFormProperties,
  clearedProxyFormProperties,
  absorbAuthorizeRejection,
} = useSalesforceProxyConnection(
  () => feed.value?.custom_base_url,
  () => feed.value?.custom_client_id,
);

const root = useTemplateRef('root');

const clientSecretPlaceholder = computed(() =>
  isStoredSecretKept.value ? MASKED_CLIENT_SECRET : undefined,
);

const isSaveDisabled = computed(
  () =>
    !canManageDataSources.value ||
    feedIsReauthorizing.value ||
    !hasChanges.value,
);

function handleFieldBlur(field: SalesforceProxyFieldType) {
  if (fields[field].trim()) touch(field);
}

const pendingAction = ref<ConfirmActionType | null>(null);
const confirmCopy = computed(() => CONFIRM_COPY[pendingAction.value ?? 'save']);

const saveSummary = computed(() => {
  const changed = changedFormProperties();
  return [
    {
      label: 'Proxy URL',
      value: changed?.base_url || feed.value?.custom_base_url || 'Not set',
    },
    {
      label: 'Client ID',
      value: changed?.client_id || feed.value?.custom_client_id || 'Not set',
    },
  ];
});

function closeConfirm() {
  pendingAction.value = null;
}

async function handleSave() {
  if (!changedFormProperties()) {
    if (fields.proxyUrl.trim() || isProxyUrlMissing.value) touch('proxyUrl');
    touch('clientId');
    touch('clientSecret');
    await focusFirstInvalidField(root);
    return;
  }
  pendingAction.value = 'save';
}

function handleRemove() {
  pendingAction.value = 'remove';
}

async function handleConfirm() {
  const formProperties =
    pendingAction.value === 'remove'
      ? clearedProxyFormProperties
      : changedFormProperties();
  if (formProperties) await reauthorizeWith(formProperties);
  closeConfirm();
}

async function reauthorizeWith(
  formProperties:
    | NonNullable<ReturnType<typeof changedFormProperties>>
    | typeof clearedProxyFormProperties,
) {
  if (!feed.value) return;
  try {
    await reauthorize(feed.value, formProperties);
  } catch (err) {
    if (absorbAuthorizeRejection(err)) return;
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Could not reauthorize feed',
      description: 'If the error persists contact support@crossbeam.com',
    });
  }
}
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-proxy-tab {
  @apply flex flex-col gap-16;
}

.c-proxy-tab__description {
  @apply text-neutral-text;
}

.c-proxy-tab__confirm--summary {
  &.o-bitts-modal.bitts-modal--confirm {
    :deep(.o-bitts-modal__content) {
      @apply block px-24 pb-24;
    }
  }
}

.c-proxy-tab__summary {
  @apply flex flex-col gap-4 text-left break-all;
}

.c-proxy-tab__summary-label {
  @apply font-bold;
}

.c-proxy-tab__actions {
  @apply flex justify-end gap-8;
}
</style>
